import { useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import "./PdfAnamneseStyle.scss";
import moment from "moment/moment";
import "moment/locale/pt";

moment.locale("pt");

function PdfAnamnese({ anamneseData, showPreviousPage }) {
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Anamnese-${anamneseData.cliente.nome}_${moment(
      new Date()
    ).format("DD-MM-YYYY")}`,
    onAfterPrint: () => {
      showPreviousPage();
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    handlePrint();
  }, [handlePrint]);

  const isPretoebranco =
    anamneseData.clinica?.configuracoes?.configuracoes?.impressaoPretoEBranco
      ?.ativo;

  // Helper: renders a label + underlined value or blank
  const Campo = ({ label, value, className = "" }) => (
    <div className={`pdf-anamnese__campo ${className}`}>
      <span className="pdf-anamnese__campo__label">{label}</span>
      <span className="pdf-anamnese__campo__valor">{value || ""}</span>
    </div>
  );

  // Helper: question row with optional "Se sim, qual?" continuation
  const Pergunta = ({ texto, resposta, comSeSimQual = false, respostaSeSimQual }) => (
    <div className="pdf-anamnese__pergunta">
      <div className="pdf-anamnese__pergunta__linha1">
        <span className="pdf-anamnese__pergunta__texto">{texto}</span>
        {!comSeSimQual && (
          <span className="pdf-anamnese__campo__valor pdf-anamnese__campo__valor--flex">
            {resposta || ""}
          </span>
        )}
        {comSeSimQual && (
          <>
            <span className="pdf-anamnese__campo__valor pdf-anamnese__campo__valor--curta">
              {resposta || ""}
            </span>
            <span className="pdf-anamnese__pergunta__sesim">Se sim, qual?</span>
            <span className="pdf-anamnese__campo__valor pdf-anamnese__campo__valor--flex">
              {respostaSeSimQual || ""}
            </span>
          </>
        )}
      </div>
      <div className="pdf-anamnese__pergunta__linha-extra" />
    </div>
  );

  const d = anamneseData;

  return (
    <div
      ref={componentRef}
      className={`pdf-anamnese-wrapper${isPretoebranco ? " pdf-wrapper--pretoebranco" : ""}`}
    >
      <div className="pdf-anamnese-body">

        {/* HEADER */}
        <div className="pdf-anamnese__cabecalho">
          <img
            src={`${process.env.REACT_APP_BACKEND_LINK}/${d.clinica.logotipo}`}
            alt={d.clinica.nome}
            className="pdf-anamnese__cabecalho__logo"
          />
          <h2 className="pdf-anamnese__title">ANAMNESE</h2>
        </div>

        {/* IDENTIFICATION */}
        <div className="pdf-anamnese__identificacao">
          <Campo label="Nome:" value={d.cliente.nome} className="pdf-anamnese__campo--full" />

          <div className="pdf-anamnese__row">
            <Campo label="Idade:" value={d.cliente.idade} className="pdf-anamnese__campo--curta" />
            <Campo label="Profissão:" value={d.cliente.profissao} className="pdf-anamnese__campo--flex" />
          </div>

          <div className="pdf-anamnese__row">
            <Campo label="Morada:" value={d.cliente.morada} className="pdf-anamnese__campo--flex" />
            <Campo label="Tel.:" value={d.cliente.contacto} className="pdf-anamnese__campo--media" />
          </div>

          <Campo label="Motivo da consulta:" value={d.motivo_consulta} className="pdf-anamnese__campo--full" />
          <Campo label="Queixa principal:" value={d.queixa_principal} className="pdf-anamnese__campo--full" />
          <div className="pdf-anamnese__blank-line" />
        </div>

        {/* CLINICAL QUESTIONS */}
        <div className="pdf-anamnese__perguntas">

          <Pergunta
            texto="Está em tratamento médico? Se sim, qual?"
            resposta={d.tratamento_medico}
          />
          <div className="pdf-anamnese__blank-line" />

          <Pergunta
            texto="Já fez alguma cirurgia?"
            resposta={d.cirurgia}
            comSeSimQual
            respostaSeSimQual={d.cirurgia_qual}
          />
          <div className="pdf-anamnese__blank-line" />

          <Pergunta
            texto="Tem alergia a algum tipo de medicamento ou anestesia?"
            resposta={d.alergia}
            comSeSimQual
            respostaSeSimQual={d.alergia_qual}
          />
          <div className="pdf-anamnese__blank-line" />

          <Pergunta
            texto="Tem alguma doença cardíaca?"
            resposta={d.doenca_cardiaca}
          />
          <div className="pdf-anamnese__blank-line" />

          <Pergunta
            texto="Tem algum problema de coagulação sanguínea?"
            resposta={d.coagulacao}
            comSeSimQual
            respostaSeSimQual={d.coagulacao_qual}
          />
          <div className="pdf-anamnese__blank-line" />

          <Pergunta texto="Possui alguma prótese cardíaca?" resposta={d.protese_cardiaca} />
          <Pergunta texto="É diabético?" resposta={d.diabetico} />
          <Pergunta texto="Tem história de sinusite?" resposta={d.sinusite} />
          <Pergunta texto="Tem Hipo ou hipertiroidismo?" resposta={d.tiroidismo} />
          <Pergunta texto="Tem convulsões ou epilepsia?" resposta={d.epilepsia} />
          <Pergunta texto="Tem alguma patologia renal?" resposta={d.patologia_renal} />
          <Pergunta texto="Tem alguma outra doença?" resposta={d.outra_doenca} />

          <div className="pdf-anamnese__pergunta">
            <span className="pdf-anamnese__pergunta__texto">
              Está grávida ou a amamentar?
            </span>
            <span className="pdf-anamnese__campo__valor pdf-anamnese__campo__valor--flex">
              {d.gravida || ""}
            </span>
          </div>
        </div>

        {/* SIGNATURES */}
        <div className="pdf-anamnese__assinaturas">
          <div className="pdf-anamnese__assinatura-bloco">
            <span className="pdf-anamnese__assinatura-label">
              Assinatura do Paciente
            </span>
            <div className="pdf-anamnese__assinatura-linha" />
          </div>

          <div className="pdf-anamnese__assinatura-bloco">
            <span className="pdf-anamnese__assinatura-label">
              Assinatura do Médico
            </span>
            {d.medico?.assinatura?.ativa &&
            d.medico?.assinatura?.autorizacoes?.atestados &&
            d.medico?.assinatura?.imagem ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_LINK}/${d.medico.assinatura.imagem}`}
                alt="Assinatura"
                className="pdf-anamnese__assinatura-img"
              />
            ) : (
              <div className="pdf-anamnese__assinatura-linha" />
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="pdf-anamnese__rodape">
          <span>
            {d.clinica.nome ? `${d.clinica.nome}: ` : "K.DENT: "}
            {d.clinica.endereco}
            {d.clinica.ilha ? ` - Ilha de ${d.clinica.ilha},` : ""}
          </span>
          <span>
            República de Cabo Verde{" "}
            {[d.clinica.contacto, d.clinica.contacto2, d.clinica.contacto3]
              .filter(Boolean)
              .join(" / ")}
            {d.clinica.email ? `, E-mail: ${d.clinica.email}` : ""}
            {d.clinica.nif ? (
              <>
                {" "}
                - <strong>NIF: {d.clinica.nif}</strong>
              </>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
}

export default PdfAnamnese;
